import React, { useState } from 'react';
import styles from './User.module.css';
import GenericIconButton from '../GenericComponents/GenericIconButton';

interface CreateAccountProps {
    onClose: () => void;
}

const CreateAccount: React.FC<CreateAccountProps> = ({ onClose }) => {
    const [newUser, setNewUser] = useState({
        realname: '',
        email: '',
        password: '',
    });
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleChange = (field: 'realname' | 'email' | 'password', value: string) => {
        setNewUser(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${process.env.REACT_APP_USERMANAGEMENT_URL}?action=create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    realname: newUser.realname,
                    email: newUser.email,
                    password: newUser.password,
                }),
            });

            if (!response.ok) {
                throw new Error(`Error creating account: ${response.statusText}`);
            }

            const result = await response.text();
            if (result === "Account Created") {
                onClose();
            } else {
                throw new Error(result || 'Failed to create account.');
            }
        } catch (err) {
            console.error('Error creating account:', err);
            setError('Failed to create account. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.popupoverlay}>
            <div className={styles.popup} role="dialog" aria-modal="true" aria-labelledby="create-account-title">
                <div className={styles.header}>
                    <h2 id="create-account-title">Create Account</h2>
                    <GenericIconButton
                        icon='light/times.svg'
                        width='18px'
                        height='18px'
                        onClick={onClose}
                        aria-label="Close"
                    />
                </div>
                <div className={styles.main}>
                    <form onSubmit={handleSubmit} className={styles.loginForm}>
                        <div className={styles.formGroup}>
                            <label htmlFor="create-realname">Name</label>
                            <input
                                type="text"
                                id="create-realname"
                                value={newUser.realname}
                                onChange={e => handleChange('realname', e.target.value)}
                                required
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="create-email">Email</label>
                            <input
                                type="email"
                                id="create-email"
                                value={newUser.email}
                                onChange={e => handleChange('email', e.target.value)}
                                required
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="create-password">Password</label>
                            <input
                                type="password"
                                id="create-password"
                                value={newUser.password}
                                onChange={e => handleChange('password', e.target.value)}
                                required
                            />
                        </div>
                        {error && (
                            <div className={styles.errorMessage}>
                                {error}
                            </div>
                        )}
                        <div className={styles.footer}>
                            <button
                                className={`${styles.loginSubmitButton}`}
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? 'Creating Account...' : 'Create Account'}
                            </button>
                            <button
                                className={`${styles.cancelButton}`}
                                type="button"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateAccount;
